import React, { useState, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { getWorkflowHistory, getDisposalIntimationById } from "../../services/disposal-intimation-service";
import { formatDateInDDMMYYYY } from "../../functions/common-functions";
import { getUsersForRole } from "../../services/user-service";
import { encryptData } from "../../utils/encryption";
import "./WorkflowHistory.css";

const WorkflowHistoryModal = ({ show, onHide, intimationNumber }) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (show && intimationNumber) {
      fetchWorkflowHistory();
    }
  }, [show, intimationNumber]);

  const getUserNamesByRole = async (roleCode, status, division, location) => {
    try {
      const users = await getUsersForRole(roleCode);
      let filteredUsers = users.filter(user => user.DeleteIndication);

      // Filter by division and location for DIVAPPROVER
      if (roleCode === "DIVAPPROVER" && status === "Pending for Divisional Approval" && division && location) {
        filteredUsers = filteredUsers.filter(user => {
          const userDivision = encryptData(user.Division, "dec");
          const userLocations = user.Location ? encryptData(user.Location, "dec").split(",").map(loc => loc.trim()) : [];
          return userDivision === division && userLocations.includes(location);
        });
      }

      return filteredUsers.map(user => encryptData(user.UserName, "dec"));
    } catch (error) {
      console.error("Error fetching users for role:", error);
      return [roleCode];
    }
  };

  const fetchWorkflowHistory = async () => {
    setLoading(true);
    try {
      // Fetch intimation data to get division
      const intimation = await getDisposalIntimationById(intimationNumber);

      const historyData = await getWorkflowHistory(intimationNumber);
      const resolvedHistory = await Promise.all(
        (Array.isArray(historyData) ? historyData : []).map(async (item, index) => {
          const isLastItem = index === historyData.length - 1;
          
          if (item.PendingWith && item.PendingWith !== item.ActionBy) {
            // For completed steps, use saved names if available
            if (!isLastItem && item.PendingWithNames) {
              return {
                ...item,
                PendingWith: item.PendingWithNames
              };
            }
            // For current/last step, get real-time users
            else {
              const initiatorLocation = intimation?.InitiatorLocation === "Other" ? intimation?.IfOtherLoc : intimation?.InitiatorLocation;
              const userNames = await getUserNamesByRole(
                item.PendingWith,
                item.Status,
                intimation?.InitiatorDivision,
                initiatorLocation
              );
              return {
                ...item,
                PendingWith: userNames.length > 0 ? userNames.join(", ") : item.PendingWith
              };
            }
          }

          return item;
        })
      );
      setHistory(resolvedHistory);
    } catch (error) {
      console.error("Error fetching workflow history:", error);
      setHistory([]);
    }
    setLoading(false);
  };



  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Workflow History - {intimationNumber}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "20px" }}>
        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="simple-workflow-timeline">
            {history.length === 0 ? (
              <div className="text-center p-4">No workflow history found</div>
            ) : (
              history.map((item, index) => (
                <div key={index} className="simple-timeline-item">
                  <div className="step-number">{index + 1}</div>
                  <div className="step-content">
                    <div className="step-header">
                      <strong>{item.Action}</strong>
                      <span className="step-date">{formatDateInDDMMYYYY(item.ActionDate)}</span>
                    </div>
                    <div className="step-details">
                      <div><strong>By:</strong> {item.ActionByName}</div>
                      {item.Status && <div><strong>Status:</strong> {item.Status}</div>}
                      {item.PendingWith && <div><strong>Pending With:</strong> {item.PendingWith}</div>}
                      {item.Remarks && (
                        <div className="step-remarks">
                          <strong>Remarks:</strong> {item.Remarks}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default WorkflowHistoryModal;